/**
 * Pre-send gating for the composer: consent, pre-chat form, customer email,
 * domain allowlist, availability, per-session message limit and rate limit.
 *
 * Split into three hooks because of where their inputs come from in ChatWidget:
 * - `useSendGatingState` runs before analytics (analytics fields include the
 *   pre-chat answers and the thread/chat requests carry consent + email);
 * - `useDomainGate` runs right after analytics (it reports `domain_blocked`);
 * - `useSendGating` runs after `useChat` (it dispatches SEND_ERROR banners and
 *   reads the transcript for the message limit).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AvailabilityConfig,
  ChatAction,
  ChatWidgetProps,
  ConsentConfig,
  Message,
  PreChatFormConfig,
  RateLimitConfig,
} from '../types';
import { isHostAllowed, isOriginAllowed } from '../utils/domain';
import { isWithinAvailability } from '../utils/availability';
import { isSafeUrl } from '../utils/urlSafety';
import { formatWidgetString } from '../utils/i18n';
import type { WidgetStrings } from '../utils/i18n';
import { useConsent } from './useConsent';
import { useCustomerEmailCapture } from './useCustomerEmailCapture';

type TrackAnalytics = (action: string, data: Record<string, unknown>) => void;

export interface UseSendGatingStateOptions {
  consent?: ConsentConfig;
  preChatForm?: PreChatFormConfig;
  customerEmailCapture: ChatWidgetProps['customerEmailCapture'];
  readSetting: (path: string) => unknown;
  strings: WidgetStrings;
}

/** Consent, pre-chat and customer-email state plus their resolved config. */
export function useSendGatingState({
  consent,
  preChatForm,
  customerEmailCapture,
  readSetting,
  strings,
}: UseSendGatingStateOptions) {
  const preChatEnabled = useMemo(() => {
    if (typeof preChatForm?.enabled === 'boolean') return preChatForm.enabled;
    return Array.isArray(preChatForm?.fields) && preChatForm.fields.length > 0;
  }, [preChatForm]);
  const [preChatData, setPreChatData] = useState<Record<string, string>>({});
  const [preChatComplete, setPreChatComplete] = useState<boolean>(() => !preChatEnabled);
  const customerEmail = useCustomerEmailCapture(customerEmailCapture);
  useEffect(() => {
    setPreChatComplete(!preChatEnabled);
  }, [preChatEnabled]);

  const consentRequired = useMemo(() => {
    if (typeof consent?.required === 'boolean') return consent.required;
    const flag = readSetting('security.consentRequired');
    return !!flag;
  }, [consent, readSetting]);
  const consentText = consent?.text || strings.consentDefaultText;
  const consentLinkText = consent?.linkText || strings.consentLearnMore;
  const safeConsentLinkUrl = isSafeUrl((consent?.linkUrl || '').trim())
    ? (consent?.linkUrl || '').trim()
    : '';
  const consentVersion = consent?.version || 'v1';
  const consentStorageKey = consent?.storageKey || 'rw_chat_consent';
  const consentRemember = consent?.remember !== false;

  const { hasConsent, setHasConsent } = useConsent({
    required: consentRequired,
    version: consentVersion,
    storageKey: consentStorageKey,
    remember: consentRemember,
  });

  return {
    preChatEnabled,
    preChatData,
    setPreChatData,
    preChatComplete,
    setPreChatComplete,
    consentRequired,
    consentText,
    consentLinkText,
    safeConsentLinkUrl,
    consentVersion,
    hasConsent,
    setHasConsent,
    customerEmail,
  };
}

export type SendGatingState = ReturnType<typeof useSendGatingState>;

export interface UseDomainGateOptions {
  allowedDomains?: string[];
  allowedOrigins?: string[];
  trackAnalytics: TrackAnalytics;
}

/** Host/origin allowlist check; reports `domain_blocked` once per config change. */
export function useDomainGate({
  allowedDomains,
  allowedOrigins,
  trackAnalytics,
}: UseDomainGateOptions) {
  const [domainAllowed, setDomainAllowed] = useState<boolean>(true);
  const [domainCheckReady, setDomainCheckReady] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host = window.location.hostname;
    const origin = window.location.origin;
    const allowed =
      isHostAllowed(host, allowedDomains || [], []) &&
      isOriginAllowed(origin, allowedOrigins || []);
    setDomainAllowed(allowed);
    setDomainCheckReady(true);
    if (!allowed) {
      trackAnalytics('domain_blocked', { host, origin });
    }
  }, [allowedDomains, allowedOrigins, trackAnalytics]);
  return { domainAllowed, domainCheckReady };
}

export interface UseSendGatingOptions {
  gating: SendGatingState;
  domainAllowed: boolean;
  availability?: AvailabilityConfig;
  offlineMessage?: string;
  maxMessagesPerSession?: number;
  rateLimit?: RateLimitConfig;
  messages: Message[];
  chatDispatch: (action: ChatAction) => void;
  trackAnalytics: TrackAnalytics;
  strings: WidgetStrings;
}

/** Derived gate flags and the pre-send guard shared by the composer and Retry. */
export function useSendGating({
  gating,
  domainAllowed,
  availability,
  offlineMessage,
  maxMessagesPerSession,
  rateLimit,
  messages,
  chatDispatch,
  trackAnalytics,
  strings,
}: UseSendGatingOptions) {
  const {
    preChatEnabled,
    preChatComplete,
    setPreChatData,
    setPreChatComplete,
    consentRequired,
    consentVersion,
    hasConsent,
    setHasConsent,
    customerEmail: {
      required: customerEmailRequired,
      isValid: customerEmailValid,
      setTouched: setCustomerEmailTouched,
    },
  } = gating;

  const [isAvailable, setIsAvailable] = useState<boolean>(() => isWithinAvailability(availability));
  useEffect(() => {
    if (!availability || availability.enabled === false) {
      setIsAvailable(true);
      return;
    }
    const tick = () => setIsAvailable(isWithinAvailability(availability));
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [availability]);

  const rateLimitRef = useRef<number[]>([]);

  const showPreChat = preChatEnabled && !preChatComplete && domainAllowed;
  const needsConsent = consentRequired && !hasConsent;
  const isOffline = availability?.enabled && !isAvailable;
  const blockWhenOffline = !!availability?.blockWhenOffline;
  const userMessageCount = useMemo(
    () => messages.filter(m => m.sender === 'user').length,
    [messages]
  );
  const messageLimitReached =
    typeof maxMessagesPerSession === 'number' && maxMessagesPerSession > 0
      ? userMessageCount >= maxMessagesPerSession
      : false;
  const inputDisabled =
    !domainAllowed ||
    showPreChat ||
    needsConsent ||
    (isOffline && blockWhenOffline) ||
    messageLimitReached;
  const statusMessage = !domainAllowed
    ? strings.domainBlockedStatus
    : messageLimitReached
      ? strings.messageLimitStatus
      : isOffline && blockWhenOffline && !offlineMessage
        ? strings.offlineStatus
        : '';

  /** Email requirement on its own (widget actions check it without the other gates). */
  const ensureCustomerEmail = useCallback((): boolean => {
    if (customerEmailRequired && !customerEmailValid) {
      setCustomerEmailTouched(true);
      chatDispatch({
        type: 'SEND_ERROR',
        error: strings.emailRequiredError,
      });
      return false;
    }
    return true;
  }, [
    customerEmailRequired,
    customerEmailValid,
    setCustomerEmailTouched,
    chatDispatch,
    strings.emailRequiredError,
  ]);

  // Pre-send policy checks shared by the composer and Retry. These are the
  // "non-message" errors that still use the top-level banner (SEND_ERROR).
  const passesSendGuards = useCallback((): boolean => {
    // First failing static guard, in the historical order.
    const blocked = !domainAllowed
      ? strings.domainBlockedStatus
      : showPreChat
        ? strings.preChatRequiredError
        : needsConsent
          ? strings.consentRequiredError
          : isOffline && blockWhenOffline
            ? offlineMessage || strings.offlineStatus
            : messageLimitReached
              ? strings.messageLimitStatus
              : '';
    if (blocked) {
      chatDispatch({ type: 'SEND_ERROR', error: blocked });
      return false;
    }
    if (!ensureCustomerEmail()) return false;

    if (rateLimit?.maxMessages && rateLimit?.windowMs) {
      const now = Date.now();
      const windowMs = rateLimit.windowMs;
      const maxMessages = rateLimit.maxMessages;
      rateLimitRef.current = rateLimitRef.current.filter(ts => now - ts < windowMs);
      if (rateLimitRef.current.length >= maxMessages) {
        const retryInMs = windowMs - (now - rateLimitRef.current[0]);
        const retryInSec = Math.ceil(retryInMs / 1000);
        chatDispatch({
          type: 'SEND_ERROR',
          error: formatWidgetString(strings.rateLimitedError, { seconds: retryInSec }),
        });
        trackAnalytics('rate_limited', { retryInMs, maxMessages, windowMs });
        return false;
      }
      rateLimitRef.current.push(now);
    }
    return true;
  }, [
    domainAllowed,
    showPreChat,
    needsConsent,
    isOffline,
    blockWhenOffline,
    offlineMessage,
    messageLimitReached,
    rateLimit,
    chatDispatch,
    trackAnalytics,
    strings,
    ensureCustomerEmail,
  ]);

  const handleConsentToggle = useCallback(
    (checked: boolean) => {
      setHasConsent(checked);
      trackAnalytics(checked ? 'consent_accepted' : 'consent_revoked', {
        version: consentVersion,
      });
      if (checked) chatDispatch({ type: 'CLEAR_ERROR' });
    },
    [setHasConsent, trackAnalytics, consentVersion, chatDispatch]
  );

  const handlePreChatSubmit = useCallback(
    (data: Record<string, string>) => {
      setPreChatData(data);
      setPreChatComplete(true);
      trackAnalytics('prechat_submitted', { fields: Object.keys(data) });
      chatDispatch({ type: 'CLEAR_ERROR' });
    },
    [setPreChatData, setPreChatComplete, trackAnalytics, chatDispatch]
  );

  // Only what ChatWidget reads; derived flags stay internal to keep the bundle lean.
  return {
    isOffline,
    showPreChat,
    inputDisabled,
    statusMessage,
    passesSendGuards,
    ensureCustomerEmail,
    handleConsentToggle,
    handlePreChatSubmit,
  };
}
